import React, { useState, useEffect } from 'react';
import { Instagram, Gift, ExternalLink, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface InstagramBonusProps {
  userId: string;
  instagramClaimed: boolean;
  onPointsClaimed: () => void;
}

const INSTAGRAM_URL = 'https://www.instagram.com/pasteleriareyeslpa/';

const InstagramBonus = ({ userId, instagramClaimed, onPointsClaimed }: InstagramBonusProps) => {
  const [claimingInstagram, setClaimingInstagram] = useState(false);
  const [instagramVisited, setInstagramVisited] = useState(false);

  // Recover visited state from localStorage on mount
  useEffect(() => {
    if (userId && !instagramClaimed) {
      const visited = localStorage.getItem(`instagram_visited_${userId}`);
      if (visited === 'true') {
        setInstagramVisited(true);
      }
    }
  }, [userId, instagramClaimed]);

  // Step 1: Open Instagram with multi-fallback system
  const handleInstagramClick = () => {
    try {
      // Method 1: Standard window.open
      const opened = window.open(INSTAGRAM_URL, '_blank', 'noopener,noreferrer');
      
      // Method 2: Fallback with temporary link if window.open fails
      if (!opened || opened.closed || typeof opened.closed === 'undefined') {
        const link = document.createElement('a');
        link.href = INSTAGRAM_URL;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      // Always mark as visited to allow step 2
      setInstagramVisited(true);
      
      // Persist state in localStorage
      if (userId) {
        localStorage.setItem(`instagram_visited_${userId}`, 'true');
      }
      
    } catch (err) {
      console.error('Error opening Instagram:', err);
      // Show copy link option as last resort
      toast.error('No se pudo abrir Instagram', {
        description: 'Usa el botón "Copiar enlace" para visitar @pasteleriareyeslpa'
      });
    }
  };

  // Copy Instagram link to clipboard
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(INSTAGRAM_URL);
      toast.success('Enlace copiado al portapapeles', {
        description: 'Pégalo en tu navegador para visitar @pasteleriareyeslpa'
      });
      setInstagramVisited(true);
      if (userId) {
        localStorage.setItem(`instagram_visited_${userId}`, 'true');
      }
    } catch (err) {
      toast.error('No se pudo copiar el enlace');
    }
  };

  // Step 2: Claim points after returning from Instagram
  const handleClaimInstagram = async () => {
    if (instagramClaimed || !instagramVisited) return;
    
    setClaimingInstagram(true);
    
    try {
      // Simulate verification delay (2 seconds)
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Call edge function to claim Instagram bonus
      const { data, error } = await supabase.functions.invoke('claim-instagram-bonus', {
        body: { user_id: userId },
      });

      if (error) throw error;

      toast.success('¡Puntos añadidos! Gracias por seguirnos', {
        description: '+10 puntos acreditados en tu cuenta',
        icon: '🎉',
      });
      onPointsClaimed();
    } catch (err) {
      toast.error('Error al reclamar los puntos');
    } finally {
      setClaimingInstagram(false);
    }
  };

  // Already claimed - show success state
  if (instagramClaimed) {
    return (
      <Card className="mb-8 border-green-500/50 bg-gradient-to-r from-card to-green-500/5">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-500 rounded-xl text-white">
              <Instagram className="h-8 w-8" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg text-green-700 dark:text-green-400 flex items-center gap-2">
                ✓ ¡Bonus de Instagram canjeado!
              </h3>
              <p className="text-muted-foreground">
                Ya has recibido tus 10 puntos por seguirnos. ¡Gracias!
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Two-step validation flow
  return (
    <Card className="mb-8 border-secondary/50 bg-gradient-to-r from-card to-secondary/5">
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="p-3 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl text-white">
            <Instagram className="h-8 w-8" />
          </div>
          <div className="flex-1">
            {!instagramVisited ? (
              <>
                <h3 className="font-semibold text-lg text-foreground">
                  ¡Consigue 10 puntos extra!
                </h3>
                <p className="text-muted-foreground">
                  Síguenos en Instagram @pasteleriareyeslpa y reclama tu recompensa.
                </p>
              </>
            ) : (
              <>
                <h3 className="font-semibold text-lg text-foreground">
                  ¿Ya nos sigues?
                </h3>
                <p className="text-muted-foreground">
                  Si ya nos sigues en Instagram, haz clic en el botón para reclamar tus puntos.
                </p>
              </>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            {!instagramVisited ? (
              <>
                <Button
                  onClick={handleInstagramClick}
                  className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700"
                >
                  <Instagram className="h-4 w-4 mr-2" />
                  Seguir en Instagram (+10 puntos)
                  <ExternalLink className="h-3 w-3 ml-2" />
                </Button>
                <Button 
                  variant="outline" 
                  size="default"
                  onClick={handleCopyLink}
                  className="border-pink-500/50 text-pink-600 hover:bg-pink-50 dark:hover:bg-pink-900/20"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copiar enlace
                </Button>
              </>
            ) : (
              <Button 
                onClick={handleClaimInstagram}
                disabled={claimingInstagram}
                className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 min-w-[200px]"
              >
                {claimingInstagram ? (
                  <>
                    <div className="h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Verificando...
                  </>
                ) : (
                  <>
                    <Gift className="h-4 w-4 mr-2" />
                    Reclamar mis 10 puntos
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default InstagramBonus;
